/**
 * PixiGameHost — the renderer-side wrapper around any VPGC game.
 *
 * Owned by the UI Engineer. The Code Lead defines the public interface here
 * (what games and HUDs may rely on); the body is for the UI Engineer to fill.
 *
 * Hard contract:
 *   - The host receives a `GameContract` plus a `GameState` (or saved JSON).
 *   - It calls `getViewModel(state)` once per draw, never reads `GameState`.
 *   - Player actions become typed `Input` values dispatched into the sim.
 *   - The host never mutates sim state directly.
 */

import { TICK_DT_SEC, ticksFromElapsed } from '@vpgc/core/fixed-timestep';
import type { GameContract } from '@vpgc/core';
import type { Recorder } from '@vpgc/playthrough';
import { Application } from 'pixi.js';

/**
 * Per-game adapter. Each game ships one of these alongside its contract; it
 * tells the host how to draw the ViewModel that *this* game produces.
 *
 * Decoupling rationale: `@vpgc/render-pixi` knows nothing about waves,
 * towers, or Beacon math. The game owns meaning; Pixi owns drawing.
 */
export interface GameRenderer<ViewModel> {
  /** Called once after the host's Pixi Application is ready. */
  mount(stage: unknown, atlas: unknown): void | Promise<void>;
  /** Called every frame with a fresh ViewModel. Must not allocate per frame. */
  draw(view: ViewModel): void;
  /** Tear down sprites, listeners, RAFs. */
  destroy(): void;
}

export interface PixiGameHostOptions<S, I, V, J> {
  /** The container element to mount the canvas into. */
  container: HTMLElement;
  /** The game's contract. */
  contract: GameContract<S, I, V, J>;
  /** The game-specific renderer. */
  renderer: GameRenderer<V>;
  /** Initial state (from `createInitialState` or `deserialize`). */
  initialState: S;
  /** "No input this tick" sentinel. */
  noOpInput: I;
  /** Renderer-specific atlas payload. */
  atlas?: unknown;
  /** Optional live input recorder. Dormant when omitted/null. */
  recorder?: Recorder<I> | null;
  /** Optional held-input sampler, called once for each fixed sim tick. */
  sampleInput?: () => I;
  /** Return the sim tick represented by state before the next step. */
  getTick?: (state: S) => number;
  /** Optional pixel-art mode. */
  pixelArt?: boolean;
}

export type ViewListener<V> = (view: V) => void;

/**
 * Host lifecycle:
 *
 *   const host = await PixiGameHost.create(opts);
 *   host.dispatch({ type: 'placeTower', kind: 'bolt', cell: 12 });
 *   host.setPaused(true);
 *   await host.destroy();
 *
 * UI Engineer fills the body. The Sim Engineer must not import this file.
 */
export class PixiGameHost<S, I, V, J> {
  private readonly app: Application;
  private readonly contract: GameContract<S, I, V, J>;
  private readonly renderer: GameRenderer<V>;
  private readonly noOpInput: I;
  private readonly sampleInput?: () => I;
  private readonly getTickFromState?: (state: S) => number;
  private readonly inputQueue: I[] = [];
  private readonly listeners = new Set<ViewListener<V>>();
  private recorder: Recorder<I> | null;
  private state: S;
  private localTick = 0;
  private rafId = 0;
  private lastTimeMs = 0;
  private carryMs = 0;
  private locallyPaused = false;
  private speed: 1 | 2 | 4 = 1;
  private destroyed = false;

  private constructor(opts: PixiGameHostOptions<S, I, V, J>, app: Application) {
    this.app = app;
    this.contract = opts.contract;
    this.renderer = opts.renderer;
    this.state = opts.initialState;
    this.noOpInput = opts.noOpInput;
    this.sampleInput = opts.sampleInput;
    this.recorder = opts.recorder ?? null;
    this.getTickFromState = opts.getTick;
  }

  static async create<S, I, V, J>(
    opts: PixiGameHostOptions<S, I, V, J>,
  ): Promise<PixiGameHost<S, I, V, J>> {
    const app = new Application();
    await app.init({
      resizeTo: opts.container,
      backgroundAlpha: 0,
      antialias: false,
      autoDensity: true,
      resolution: Math.min(globalThis.devicePixelRatio || 1, 2),
    });

    if (opts.pixelArt) {
      app.canvas.style.imageRendering = 'pixelated';
    }

    app.canvas.className = 'vpgc-pixi-canvas';
    opts.container.appendChild(app.canvas);
    await opts.renderer.mount(app.stage, opts.atlas);

    const host = new PixiGameHost(opts, app);
    host.draw();
    host.start();
    return host;
  }

  /** Dispatch a typed Input into the sim. Buffered until the next tick boundary. */
  dispatch(input: I): void {
    this.inputQueue.push(input);
  }

  /** Pause/unpause the simulation loop. Pure render keeps running. */
  setPaused(paused: boolean): void {
    this.locallyPaused = paused;
  }

  /** Set sim speed multiplier (1, 2, 4). Affects ticks-per-frame, not dt. */
  setSpeed(speed: 1 | 2 | 4): void {
    this.speed = speed;
  }

  setRecorder(recorder: Recorder<I> | null): void {
    this.recorder = recorder;
  }

  currentTick(): number {
    return this.currentInputTick();
  }

  /** Snapshot the current GameState through `serialize`. For save/export. */
  snapshot(): J {
    return this.contract.serialize(this.state);
  }

  getViewModel(): V {
    return this.contract.getViewModel(this.state);
  }

  subscribe(listener: ViewListener<V>): () => void {
    this.listeners.add(listener);
    listener(this.getViewModel());
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Tear down. */
  async destroy(): Promise<void> {
    this.destroyed = true;
    if (this.rafId !== 0) cancelAnimationFrame(this.rafId);
    this.inputQueue.length = 0;
    this.listeners.clear();
    this.renderer.destroy();
    this.app.destroy(true);
  }

  private start(): void {
    this.lastTimeMs = performance.now();
    const frame = (timeMs: number) => {
      if (this.destroyed) return;
      this.tick(timeMs);
      this.draw();
      this.rafId = requestAnimationFrame(frame);
    };
    this.rafId = requestAnimationFrame(frame);
  }

  private tick(timeMs: number): void {
    const elapsedMs = Math.min(250, Math.max(0, timeMs - this.lastTimeMs));
    this.lastTimeMs = timeMs;

    if (this.locallyPaused) {
      this.flushInputs();
      this.carryMs = 0;
      return;
    }

    const next = ticksFromElapsed(elapsedMs, this.carryMs);
    this.carryMs = next.carryMs;
    const ticks = Math.min(20, next.ticks * this.speed);
    for (let i = 0; i < ticks; i++) {
      const input = this.inputQueue.shift() ?? this.sampleInput?.() ?? this.noOpInput;
      this.stepWithInput(input);
    }
    this.flushInputs();
  }

  private flushInputs(): void {
    while (this.inputQueue.length > 0) {
      const input = this.inputQueue.shift() ?? this.noOpInput;
      this.stepWithInput(input);
    }
  }

  private stepWithInput(input: I): void {
    if (this.recorder !== null && shouldRecordInput(input, this.noOpInput)) {
      this.recorder.record(this.currentInputTick(), input);
    }
    this.state = this.contract.step(this.state, input, TICK_DT_SEC);
    this.localTick++;
  }

  private currentInputTick(): number {
    return this.getTickFromState?.(this.state) ?? this.localTick;
  }

  private draw(): void {
    const view = this.contract.getViewModel(this.state);
    this.renderer.draw(view);
    for (const listener of this.listeners) listener(view);
  }
}

function shouldRecordInput<I>(input: I, noOpInput: I): boolean {
  if (typeof input === 'object' && input !== null && 'type' in input) {
    return (input as { type?: unknown }).type !== 'none';
  }
  return input !== noOpInput;
}
